import { useEffect, useMemo, useRef } from 'react';

// 5x7 dot-matrix glyphs (1 = dot, 0 = empty)
const GLYPHS: Record<string, number[][]> = {
  S: [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 0],
    [1, 0, 0, 0, 0],
    [0, 1, 1, 1, 0],
    [0, 0, 0, 0, 1],
    [0, 0, 0, 0, 1],
    [1, 1, 1, 1, 0],
  ],
  T: [
    [1, 1, 1, 1, 1],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
  ],
  U: [
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  D: [
    [1, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 1, 1, 1, 0],
  ],
  I: [
    [1, 1, 1, 1, 1],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [1, 1, 1, 1, 1],
  ],
  O: [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  ' ': [
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
  ],
};

const ROWS = 7;
const LETTER_COLS = 5;
const LETTER_GAP = 1;

interface Particle {
  // target position (resting place)
  tx: number;
  ty: number;
  // current position
  x: number;
  y: number;
  // velocity
  vx: number;
  vy: number;
  // visual radius
  r: number;
  // 1 = at rest at target; auto-springs back via physics
}

interface DotHeroTitleProps {
  text?: string;
}

export default function DotHeroTitle({ text = 'STUDIO' }: DotHeroTitleProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef<{ x: number; y: number; active: boolean }>({
    x: -9999,
    y: -9999,
    active: false,
  });
  const dimRef = useRef({ w: 0, h: 0, cell: 0 });
  const rafRef = useRef<number | null>(null);

  // Build dot list from text
  const dotMatrix = useMemo(() => {
    const chars = text.toUpperCase().split('');
    const totalCols =
      chars.length * LETTER_COLS + Math.max(0, chars.length - 1) * LETTER_GAP;
    const positions: { col: number; row: number }[] = [];
    let cursor = 0;
    chars.forEach((ch, ci) => {
      const g = GLYPHS[ch] || GLYPHS[' '];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < LETTER_COLS; c++) {
          if (g[r][c] === 1) positions.push({ col: cursor + c, row: r });
        }
      }
      cursor += LETTER_COLS + (ci < chars.length - 1 ? LETTER_GAP : 0);
    });
    return { positions, totalCols };
  }, [text]);

  // (Re)build particles whenever size or text changes
  const rebuildParticles = () => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const rect = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Layout the matrix centered
    const padX = rect.width * 0.06;
    const padY = rect.height * 0.12;
    const availableW = rect.width - padX * 2;
    const availableH = rect.height - padY * 2;

    const cellByW = availableW / dotMatrix.totalCols;
    const cellByH = availableH / ROWS;
    const cell = Math.min(cellByW, cellByH);

    const gridW = cell * dotMatrix.totalCols;
    const gridH = cell * ROWS;
    const originX = (rect.width - gridW) / 2 + cell / 2;
    const originY = (rect.height - gridH) / 2 + cell / 2;

    dimRef.current = { w: rect.width, h: rect.height, cell };

    // Sub-divide each glyph cell into a 2x2 cluster for denser particles
    const SUB = 2;
    const subStep = cell / SUB;
    const subOffset = -((SUB - 1) / 2) * subStep;
    const particles: Particle[] = [];
    dotMatrix.positions.forEach((p) => {
      const cx = originX + p.col * cell;
      const cy = originY + p.row * cell;
      for (let sy = 0; sy < SUB; sy++) {
        for (let sx = 0; sx < SUB; sx++) {
          const tx = cx + subOffset + sx * subStep;
          const ty = cy + subOffset + sy * subStep;
          particles.push({
            tx,
            ty,
            x: tx,
            y: ty,
            vx: 0,
            vy: 0,
            r: subStep * 0.7,
          });
        }
      }
    });
    particlesRef.current = particles;
  };

  useEffect(() => {
    rebuildParticles();
    const onResize = () => rebuildParticles();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotMatrix]);

  // Animation loop with physics
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const SPRING = 0.045; // pull back to target
    const FRICTION = 0.86; // damping
    const REPEL_RADIUS_FACTOR = 4.5; // multiples of cell size
    const REPEL_FORCE = 0.55;

    const tick = () => {
      const { w, h, cell } = dimRef.current;
      if (w === 0 || h === 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      ctx.clearRect(0, 0, w, h);

      const repelR = cell * REPEL_RADIUS_FACTOR;
      const mouse = mouseRef.current;

      const particles = particlesRef.current;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Mouse repulsion
        if (mouse.active) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const distSq = dx * dx + dy * dy;
          const r2 = repelR * repelR;
          if (distSq < r2 && distSq > 0.001) {
            const dist = Math.sqrt(distSq);
            const force = (1 - dist / repelR) * REPEL_FORCE * cell;
            p.vx += (dx / dist) * force;
            p.vy += (dy / dist) * force;
          }
        }

        // Spring back to target (auto-restore with inertia)
        p.vx += (p.tx - p.x) * SPRING;
        p.vy += (p.ty - p.y) * SPRING;

        // Damping
        p.vx *= FRICTION;
        p.vy *= FRICTION;

        // Integrate
        p.x += p.vx;
        p.y += p.vy;

        // Draw
        const dist = Math.hypot(p.x - p.tx, p.y - p.ty);
        const traveled = Math.min(dist / (cell * 6), 1);
        const radius = p.r * (1 + traveled * 0.6);
        const alpha = 1 - traveled * 0.35;
        ctx.beginPath();
        ctx.fillStyle = `rgba(234, 88, 12, ${alpha})`;
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      active: true,
    };
  };

  const handleLeave = () => {
    mouseRef.current = { x: -9999, y: -9999, active: false };
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full"
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
    >
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}